import { lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { urls } from '@repo/app-constants';

import useFeature from 'services/features/useFeature';

import Account from 'views/Account/Account';
import { Subscriptions } from 'views/Account/Subscriptions/Subscriptions';
import SubscriptionsCancellation from 'views/Account/Subscriptions/SubscriptionsCancellation';
import { ActionPlan, ActionPlanRoutes } from 'views/ActionPlan';
import { BookmarksRoutes } from 'views/ActionPlan/MyList/MyList';
import { Guides } from 'views/Guides/Guides';
import { KitsRoutes } from 'views/Kits/KitsPage';
import { ProfileRoutes } from 'views/Profiles/Profile';
import { Referrals } from 'views/Referrals/Referrals';
import { ResultsRoutes } from 'views/Results';
import { ReviewRoutes } from 'views/Review/Review';
import Typeform from 'views/Typeform/Typeform';
import TypeformSurvey from 'views/TypeformSurvey/TypeformSurvey';

import CatchAllRoute from 'routes/CatchAllRoute';
import NoAuthRoute from 'routes/NoAuthRoute';
import ProtectedRoute from 'routes/ProtectedRoute';
import FamilyPage from 'views/FamilyPage/FamilyPage';
import Home from 'views/Home/Home';
import ImpersonateRoute from './ImpersonateRoute';
import NoAuthRouteMinimalLayout from './NoAuthMinimalRoute';

import { LINKS } from 'assets';
import PartnerKitActivationContainer from 'views/ActivateKit/Partners/PartnerKitActivationContainer';
import PartnerRecs from 'views/PartnerRecs/PartnerRecs';
import KitActivationStepNoAuth from '../views/ActivateKit/KitActivationStepNoAuth';

const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
};

const MonitoringDebug = lazy(() => import('views/Debug/MonitoringDebug'));
const Information = lazy(() => import('views/Information/Information'));
const HelpPage = lazy(() => import('views/Help/Help'));
const Nps = lazy(() => import('views/Auth/Nps/Nps'));
const Consults = lazy(() => import('views/Consults/Consults'));
const ConsultConfirmation = lazy(
  () => import('views/Consults/ConsultConfirmation/ConsultConfirmation'),
);
const ConsultsInfoPage = lazy(
  () => import('views/Consults/ConsultsInfo/ConsultsInfoPage'),
);
const ConsultNotes = lazy(
  () => import('views/Consults/ConsultNotes/ConsultNotes'),
);
const SignUpForm = lazy(() => import('views/Auth/SignUpForm/SignUpForm'));
const Login = lazy(() => import('views/Auth/Login/Login'));
const AuthLayout = lazy(
  () => import('components/Layout/AuthLayout/AuthLayout'),
);
const ForgotPasswordForm = lazy(
  () => import('views/Auth/ForgotPasswordForm/ForgotPasswordForm'),
);
const UpdatePasswordForm = lazy(
  () => import('views/Auth/UpdatePasswordForm/UpdatePasswordForm'),
);

const VerifyAccount = lazy(
  () => import('views/Auth/Verification/VerifyAccount'),
);
const EmailVerificationPage = lazy(
  () => import('views/Auth/Verification/EmailVerificationPage'),
);
const AccountPasswordPage = lazy(
  () => import('views/Auth/AccountPassword/AccountPasswordPage'),
);
const ResponsiveLayout = lazy(
  () => import('components/Layout/ResponsiveLayout/ResponsiveLayout'),
);
const PurchaseKitUpsell = lazy(
  () => import('views/PurchaseKitUpsell/PurchaseKitUpsell'),
);
const PaymentSuccessful = lazy(
  () => import('views/PaymentSuccessful/PaymentSuccessful'),
);

export const THRoutes = () => {
  const { isFeatureEnabled: isPurchaseKitUpsellEnabled } = useFeature(
    'purchase_kit_upsell',
  );
  const {
    isFeatureEnabled: isTinyPlusUpsellMomentsEnabled,
    variables: tinyPlusUpsellMomentsVariables,
  } = useFeature('tiny_plus_upsell_moments');
  const isMembershipRouteEnabled =
    isTinyPlusUpsellMomentsEnabled &&
    Boolean(tinyPlusUpsellMomentsVariables?.enable_membership);
  return (
    <Routes>
      <Route path='/' element={<Home />} />

      <Route path='/impersonate' element={<ImpersonateRoute />} />

      <Route element={<NoAuthRoute />}>
        <Route element={<AuthLayout />}>
          <Route path='/login' element={<Login />} />
          <Route path='/register' element={<SignUpForm />} />
          <Route path='/check-email' element={<VerifyAccount />} />
          <Route
            path='/email-verification'
            element={<EmailVerificationPage />}
          />
          <Route path='/reset-password' element={<ForgotPasswordForm />} />
          <Route path='/update-password' element={<UpdatePasswordForm />} />
        </Route>
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route element={<ResponsiveLayout />}>
          <Route path='/information/:kitId' element={<Information />} />
          <Route path='/family' element={<FamilyPage />} />
          <Route path='/results/*' element={<ResultsRoutes />} />
          <Route path='/action-plan/:kitId/*' element={<ActionPlanRoutes />} />
          <Route path='/action-plan' element={<ActionPlan />} />
          <Route path='/partner-recs/:partnerId' element={<PartnerRecs />} />
          <Route path='/bookmarks/*' element={<BookmarksRoutes />} />
          <Route path='/guides' element={<Guides />} />
          <Route path='/refer-a-friend' element={<Referrals />} />
          <Route path='/profile/*' element={<ProfileRoutes />} />
          <Route
            path='/book-a-consult'
            element={<Navigate to='/consults' replace />}
          />
          <Route path='/consults' element={<Consults />} />
          <Route
            path='/consults/confirmation'
            element={<ConsultConfirmation />}
          />
          <Route path='/consults/info' element={<ConsultsInfoPage />} />
          <Route path='/consults/:kitId' element={<ConsultNotes />} />
          <Route path='/consults/book' element={<Navigate to='/' replace />} />
          <Route path='/help' element={<HelpPage />} />
          <Route path='/kits/*' element={<KitsRoutes />} />
          <Route path='/review/*' element={<ReviewRoutes />} />
          <Route path='/account/subscriptions' element={<Subscriptions />} />
          <Route
            path='/account/subscriptions/:subscriptionId/cancellation'
            element={<SubscriptionsCancellation />}
          />
          <Route path='/account/*' element={<Account />} />

          {isMembershipRouteEnabled && (
            <Route
              path='/membership'
              element={<ExternalRedirect to={urls.TINY_PLUS_UPGRADE_OFFER} />}
            />
          )}

          {isPurchaseKitUpsellEnabled && (
            <Route path={LINKS.purchaseKit} element={<PurchaseKitUpsell />} />
          )}

          <Route
            path='/typeform/:tinyAccountId/:kitId/:typeformId/:surveyType'
            element={<Typeform />}
          />
          <Route
            path='/typeform/:tinyAccountId/:kitId/:typeformId'
            element={<TypeformSurvey />}
          />
        </Route>
      </Route>

      <Route element={<NoAuthRouteMinimalLayout />}>
        <Route path='/account-password' element={<AccountPasswordPage />} />
      </Route>
      <Route path='/nps' element={<Nps />} />
      <Route path='/debug/monitoring' element={<MonitoringDebug />} />
      <Route path='/activate' element={<KitActivationStepNoAuth />} />
      <Route
        path='/payment-successful/:orderNumber'
        element={<PaymentSuccessful />}
      />
      <Route
        path='/partners/:kitId'
        element={<PartnerKitActivationContainer />}
      />
      <Route path='/*' element={<CatchAllRoute />} />
    </Routes>
  );
};
